import { ReactElement, PropsWithChildren } from "react";
import styles from "./HubManagementDialogContentLayout.module.less";
import {
	BusyIndicator,
	ContextActionBar,
	PageContentTitle,
	RoundButton,
	ToolbarAction
} from "client/Components";
import { setTrackingId } from "client/Misc/utils";

export type HubManagementDialogContentLayoutProps<TActionContext> = {
	title: string;
	isMobile: boolean;
	isResponsive: boolean;
	trackingId: string;
	isBusy?: boolean;
} & (
	{
		actions?: undefined;
		actionContext?: undefined;
		hasSelection?: undefined;
	}
	|
	{
		actions: readonly ToolbarAction<TActionContext>[];
		actionContext: TActionContext;
		hasSelection?: boolean;
	}
);

export const HubManagementDialogContentLayout = <TActionContext,>(
	props: PropsWithChildren<HubManagementDialogContentLayoutProps<TActionContext>>
): ReactElement => {
	const {
		children,
		title,
		actions,
		actionContext,
		hasSelection = false,
		isMobile,
		trackingId,
		isBusy = false
	} = props;

	const renderHeader = (): ReactElement => {
		if (isMobile) {
			return (
				<h3>{title}</h3>
			);
		}

		if (actions === undefined) {
			return (
				<PageContentTitle
					title={title}
				/>
			);
		}

		return hasSelection ? (
			<ContextActionBar
				actions={actions}
				context={actionContext}
				trackingId="action-bar"
				horizontalAlignment="page-content"
			/>
		) : (
			<PageContentTitle
				title={title}
				actions={actions}
				actionContext={actionContext}
			/>
		);
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{isBusy && <BusyIndicator />}
			{isMobile && actions !== undefined && (
				<div>
					<ContextActionBar
						actions={actions}
						context={actionContext}
						trackingId="action-bar"
						hasBorderBottom
					/>
				</div>
			)}
			<div className={styles.contentWithHeader}>
				<header className={styles.title}>
					{renderHeader()}
				</header>
				<div className={styles.content}>
					{children}
				</div>
			</div>
		</div>
	);
};
HubManagementDialogContentLayout.displayName="HubManagementDialogContentLayout";

const HubManagementDialogContentLayoutFilter = (props: PropsWithChildren<{}>): ReactElement => {
	const { children } = props;

	return (
		<div className={styles.filter}>
			{children}
		</div>
	);
};
HubManagementDialogContentLayoutFilter.displayName = "HubManagementDialogContentLayout.Filter";

const HubManagementDialogContentLayoutTable = (props: PropsWithChildren<{}>): ReactElement => {
	const { children } = props;

	return (
		<div className={styles.table}>
			{children}
		</div>
	);
};
HubManagementDialogContentLayoutTable.displayName = "HubManagementDialogContentLayout.Table";

type HubManagementDialogContentLayoutMainButtonProps = {
	onClick: () => void;
	trackingId: string;
};

const HubManagementDialogContentLayoutMainButton = (props: HubManagementDialogContentLayoutMainButtonProps): ReactElement => {
	const { onClick, trackingId } = props;

	return (
		<>
			<div className={styles.mainButtonContainer}>
				<RoundButton
					icon="plus"
					onClick={onClick}
					trackingId={trackingId}
				/>
			</div>
			<div className={styles.mainButtonPreserveSpace}/>
		</>
	);
};
HubManagementDialogContentLayoutMainButton.displayName = "HubManagementDialogContentLayout.MainButton";

HubManagementDialogContentLayout.Filter = HubManagementDialogContentLayoutFilter;
HubManagementDialogContentLayout.Table = HubManagementDialogContentLayoutTable;
HubManagementDialogContentLayout.MainButton = HubManagementDialogContentLayoutMainButton;